import React, { useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  DescriptionOutlined,
  ErrorOutlineOutlined,
  EventOutlined,
  InfoOutlined,
  ListAltOutlined,
  PriorityHighRounded,
  ReplayRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { esiValidationHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { RecordHeaderCard, StatCardGrid, StatutoryList, ToolbarAddButton, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { FIX_ROUTES, SEVERITY_COLORS } from "./esiReportsData";

// Validation (R5) and the Validation Issue drawer (R5a). Checks run by
// themselves at the events in spec §6 and again with Re-check now; each run
// replaces the issues for the month and codes it covered. Blocking issues
// stop the ESIC file for their code; warnings and info do not.

const ALL = "all";
const { pink, orange, blue, green } = detailAccentPalette;

const IssuePanel = ({ open, issue, onClose }) => {
  const history = useHistory();
  if (!issue) return null;
  const route = FIX_ROUTES[issue.fix_in];
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Validation Issue"
      subtitle={`${issue.severity} · ${issue.severity === "Blocking" && issue.code ? `ESIC file ${issue.code_label}` : issue.code_label}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {route ? <PrimaryButton onClick={() => history.push(route.path)}>{route.label}</PrimaryButton> : null}
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(issue.name)}
          title={issue.name}
          chip={<RequestStatusChip status={issue.severity} colorCode={SEVERITY_COLORS[issue.severity]} />}
          meta={[issue.emp_id, issue.department, issue.code_label].filter(Boolean).join(" · ")}
          tone={issue.severity === "Blocking" ? "danger" : issue.severity === "Warning" ? "warning" : "teal"}
        />
        <RequestDetailsSection
          title="ISSUE"
          fields={[
            { icon: PriorityHighRounded, label: "Problem", value: issue.problem, accent: pink },
            ...(issue.detail ? [{ icon: EventOutlined, label: issue.detail.label, value: issue.detail.value, accent: orange, multiline: true }] : []),
            { icon: DescriptionOutlined, label: "Effect", value: issue.effect, accent: blue, multiline: true },
          ]}
        />
        {issue.steps.length ? (
          <RequestDetailsSection
            title="HOW TO FIX"
            fields={issue.steps.map(([title, detail], index) => ({
              icon: () => (
                <Mui.Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                  {index + 1}
                </Mui.Box>
              ),
              label: title,
              value: detail,
              accent: green,
            }))}
          />
        ) : (
          <RequestInfoBanner body="Nothing to fix — this is for your information." />
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const EsiValidationTab = ({ issues, lastCheck, preview, onRecheck }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_validation_saved_views",
    fullHeaders: esiValidationHeader,
  });

  const rows = card === ALL ? issues : issues.filter((item) => item.severity === card);
  const count = (severity) => issues.filter((item) => item.severity === severity).length;

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>{initialsOf(row.name)}</Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "severity":
        return <RequestStatusChip status={row.severity} colorCode={SEVERITY_COLORS[row.severity]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      {preview ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner tone="warning" title="Preview" body="Payroll for this month is not finalised. These checks show what to fix before finalising; the ESIC file opens after finalisation." />
        </Mui.Box>
      ) : null}
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: ALL, title: "All issues", value: issues.length, icon: ListAltOutlined, tone: "blue" },
          { key: "Blocking", title: "Blocking", value: count("Blocking"), icon: ErrorOutlineOutlined, tone: "red" },
          { key: "Warning", title: "Warning", value: count("Warning"), icon: WarningAmberRounded, tone: "amber" },
          { key: "Info", title: "Info", value: count("Info"), icon: InfoOutlined, tone: "green" },
        ]}
      />
      <Mui.Typography sx={{ ...designType.captionText, mb: 1 }}>
        Last checked {moment(lastCheck.at).format("DD MMM YYYY, HH:mm")} · trigger: {lastCheck.trigger}
      </Mui.Typography>
      <StatutoryList
        title="ESI Validation"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="issues"
        searchPlaceholder="Search"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        action={
          <ToolbarAddButton
            startIcon={<ReplayRounded />}
            onClick={() => {
              onRecheck();
              showToast("success", "Validation checked again.");
            }}
          >
            Re-check now
          </ToolbarAddButton>
        }
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Checks run by themselves when payroll is reviewed or finalised and when IP numbers, codes, locations or reason mappings change. Each run replaces the issues for its month and codes. Blocking issues stop the ESIC file for that code." />
      </Mui.Box>
      <IssuePanel open={Boolean(viewId)} issue={issues.find((item) => item.id === viewId)} onClose={() => setViewId(null)} />
    </>
  );
};
